// covered-by: frontends/carrie/src/ask-v2/shell/__tests__/AskV2SidebarRecents.test.tsx
import { MAX_ASK_PUNTT_CHAT_TITLE_LENGTH } from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttProjectReviewState } from "@mg/schemas/src/christo/askPunttProject";
import { CircleNotch, PencilSimple, Trash } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Link, Text } from "@radix-ui/themes";
import {
  Link as NavLink,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  askV2ChatNavLink,
  askV2ProjectNavLink,
  isAskV2ChatNavActive,
  isAskV2ProjectNavActive,
  isProjectPersonalChat,
} from "./askV2ChatNavigation";

import { UnreadDot } from "../../components/ask-puntt-list/UnreadDot";
import {
  formatChatMeta,
  ProjectRecentMeta,
  RecentsKindIcon,
} from "../../components/sidebar/recentsRowParts";
import {
  SIDEBAR_ACTIVE_ROW_CLASS,
  SIDEBAR_ITEM_META_CLASS,
  SIDEBAR_ITEM_META_STYLE,
  SIDEBAR_SECTION_HEADER_INDENT,
  SidebarRowTitle,
} from "../../components/sidebar/sidebarItemStyles";
import { useSidebar } from "../../components/sidebar/useSidebar";
import { useAppSelector } from "../../utils/hooks";
import {
  ASK_PUNTT_UNREAD_LIST_POLL_MS,
  useAskPunttChats,
  useDeleteAskPunttChat,
  useRenameAskPunttChat,
} from "../../utils/queries/askPuntt";
import {
  useAskPunttProjects,
  useDeleteAskPunttProject,
  useRenameAskPunttProject,
} from "../../utils/queries/askPunttProjects";
import { ASK_V2_ASK_HOME_PATH, ASK_V2_PROJECT_PATH } from "../paths";

const SIDEBAR_RECENTS_LIMIT = 10;

const SIDEBAR_RECENTS_ACTIONS_WRAP =
  "absolute right-1.5 top-1.5 z-[2] flex items-center gap-0.5";
// why: `hidden` (display:none) until hover took Rename/Delete out of the tab
// order AND the accessibility tree, so a keyboard-only or screen-reader user
// could never rename or delete a recent — and a `focus-visible:` reveal can
// never fire on an element that cannot receive focus in the first place.
// Reveal with opacity instead (mirrors BreadcrumbInlineRename's pencil): the
// buttons stay focusable and surface on focus as well as row hover. The
// actions wrapper is absolutely positioned, so keeping them laid out costs the
// row no space.
const SIDEBAR_RECENTS_ACTION_BTN =
  "flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm opacity-0 transition-opacity focus-visible:opacity-100 group-hover/chat-item:opacity-100";
const SIDEBAR_RECENTS_ROW_CLASS = `group/chat-item relative flex w-full items-center gap-4 rounded-md py-1.5 ${SIDEBAR_SECTION_HEADER_INDENT} pr-2 transition-colors hover:bg-puntt-neutral-gray-3`;

const MAX_ASK_PUNTT_PROJECT_NAME_LENGTH = 200;

const toRecentTime = (value: string | Date | null | undefined) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

interface RecentProject {
  _id: string;
  name: string;
  reviewState?: AskPunttProjectReviewState;
  role?: "owner" | "member";
  ownerId?: string;
  hasUnread?: boolean;
}

function RecentProjectRow({
  project,
  active,
  canModify,
  onRequestDelete,
}: {
  project: RecentProject;
  active: boolean;
  canModify: boolean;
  onRequestDelete: (projectId: string) => void;
}) {
  const { t } = useTranslation("common");
  const renameMutation = useRenameAskPunttProject(project._id);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(project.name);
  const doneRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isRenaming) setDraft(project.name);
  }, [project.name, isRenaming]);

  function startRename() {
    doneRef.current = false;
    cancelledRef.current = false;
    setDraft(project.name);
    setIsRenaming(true);
  }

  function commit() {
    if (doneRef.current || cancelledRef.current) return;
    doneRef.current = true;
    const next = draft.trim();
    setIsRenaming(false);
    if (!next || next === project.name.trim()) {
      setDraft(project.name);
      return;
    }
    renameMutation.mutate(next);
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(project.name);
    setIsRenaming(false);
  }

  return (
    <div
      className={cx(
        SIDEBAR_RECENTS_ROW_CLASS,
        active && SIDEBAR_ACTIVE_ROW_CLASS,
      )}
      data-testid="sidebar-ask-puntt-recent-item"
      data-kind="project"
      data-project-id={project._id}
      data-active={active ? "true" : "false"}
    >
      {isRenaming ? (
        <input
          autoFocus
          value={draft}
          onFocus={(e) => e.target.select()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          maxLength={MAX_ASK_PUNTT_PROJECT_NAME_LENGTH}
          aria-label={t("nav.projectName")}
          data-testid="sidebar-ask-puntt-rename-input"
          className="w-full min-w-0 rounded border border-puntt-accent-9 bg-base-white px-1.5 py-0.5 text-sm text-puntt-neutral-gray-12 outline-none focus:ring-1 focus:ring-puntt-accent-8"
        />
      ) : (
        <Link asChild size="2" className="flex-1 overflow-hidden">
          <NavLink
            {...askV2ProjectNavLink(project._id)}
            className="flex w-full items-center gap-2 overflow-hidden"
          >
            <RecentsKindIcon kind="project" id={project._id} />
            <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <span className="flex items-center gap-2">
                <SidebarRowTitle
                  name={project.name}
                  testId="sidebar-ask-puntt-recent-title"
                />
                {project.hasUnread ? (
                  <UnreadDot
                    testId={`sidebar-ask-puntt-recent-unread-${project._id}`}
                    ariaLabel={t("nav.unread")}
                  />
                ) : null}
              </span>
              <ProjectRecentMeta state={project.reviewState} />
            </span>
          </NavLink>
        </Link>
      )}
      {canModify && !isRenaming ? (
        <div className={SIDEBAR_RECENTS_ACTIONS_WRAP}>
          <button
            type="button"
            className={cx(
              SIDEBAR_RECENTS_ACTION_BTN,
              "text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-4",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startRename();
            }}
            aria-label={t("nav.renameProject")}
            data-testid="sidebar-ask-puntt-rename-recent"
            disabled={renameMutation.isPending}
          >
            {renameMutation.isPending ? (
              <CircleNotch size={10} className="animate-spin" aria-hidden />
            ) : (
              <PencilSimple size={10} aria-hidden />
            )}
          </button>
          <button
            type="button"
            className={cx(
              SIDEBAR_RECENTS_ACTION_BTN,
              "text-puntt-neutral-gray-11 hover:bg-puntt-red-9 hover:text-base-white",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRequestDelete(project._id);
            }}
            aria-label={t("nav.deleteProject")}
            data-testid="sidebar-ask-puntt-delete-recent"
          >
            <Trash size={10} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface RecentChat {
  _id: string;
  title: string;
  userId?: string;
  ownerName?: string;
  updatedAt?: string | Date | null;
  hasUnread?: boolean;
}

function RecentChatRow({
  chat,
  active,
  isOwnChat,
  onRequestDelete,
}: {
  chat: RecentChat;
  active: boolean;
  isOwnChat: boolean;
  onRequestDelete: (chatId: string) => void;
}) {
  const { t } = useTranslation("common");
  const renameMutation = useRenameAskPunttChat();
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(chat.title);
  const doneRef = useRef(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isRenaming) setDraft(chat.title);
  }, [chat.title, isRenaming]);

  function startRename() {
    doneRef.current = false;
    cancelledRef.current = false;
    setDraft(chat.title);
    setIsRenaming(true);
  }

  function commit() {
    if (doneRef.current || cancelledRef.current) return;
    doneRef.current = true;
    const next = draft.trim();
    setIsRenaming(false);
    if (!next || next === chat.title.trim()) {
      setDraft(chat.title);
      return;
    }
    renameMutation.mutate({ chatId: chat._id, title: next });
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(chat.title);
    setIsRenaming(false);
  }

  const updatedAtForMeta =
    chat.updatedAt instanceof Date
      ? chat.updatedAt.toISOString()
      : (chat.updatedAt ?? undefined);
  const metaText = formatChatMeta(
    updatedAtForMeta,
    isOwnChat ? null : chat.ownerName,
  );

  return (
    <div
      className={cx(
        SIDEBAR_RECENTS_ROW_CLASS,
        active && SIDEBAR_ACTIVE_ROW_CLASS,
      )}
      data-testid="sidebar-ask-puntt-recent-chat-item"
      data-kind="chat"
      data-chat-id={chat._id}
      data-active={active ? "true" : "false"}
    >
      {isRenaming ? (
        <input
          autoFocus
          value={draft}
          onFocus={(e) => e.target.select()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          maxLength={MAX_ASK_PUNTT_CHAT_TITLE_LENGTH}
          aria-label={t("nav.chatName")}
          data-testid="sidebar-ask-puntt-rename-chat-input"
          className="w-full min-w-0 rounded border border-puntt-accent-9 bg-base-white px-1.5 py-0.5 text-sm text-puntt-neutral-gray-12 outline-none focus:ring-1 focus:ring-puntt-accent-8"
        />
      ) : (
        <Link asChild size="2" className="flex-1 overflow-hidden">
          <NavLink
            {...askV2ChatNavLink(chat)}
            className="flex w-full items-center gap-2 overflow-hidden"
          >
            <RecentsKindIcon kind="chat" id={chat._id} />
            <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <span className="flex items-center gap-2">
                <SidebarRowTitle
                  name={chat.title}
                  testId="sidebar-ask-puntt-recent-title"
                />
                {chat.hasUnread ? (
                  <UnreadDot
                    testId={`sidebar-ask-puntt-recent-unread-${chat._id}`}
                    ariaLabel={t("nav.unread")}
                  />
                ) : null}
              </span>
              {metaText ? (
                <span
                  className={SIDEBAR_ITEM_META_CLASS}
                  style={SIDEBAR_ITEM_META_STYLE}
                  data-testid="sidebar-ask-puntt-recent-meta"
                >
                  <span className="truncate">{metaText}</span>
                </span>
              ) : null}
            </span>
          </NavLink>
        </Link>
      )}
      {isOwnChat && !isRenaming ? (
        <div className={SIDEBAR_RECENTS_ACTIONS_WRAP}>
          <button
            type="button"
            className={cx(
              SIDEBAR_RECENTS_ACTION_BTN,
              "text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-4",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startRename();
            }}
            aria-label={t("nav.renameChat")}
            data-testid="sidebar-ask-puntt-rename-chat-recent"
            disabled={renameMutation.isPending}
          >
            {renameMutation.isPending ? (
              <CircleNotch size={10} className="animate-spin" aria-hidden />
            ) : (
              <PencilSimple size={10} aria-hidden />
            )}
          </button>
          <button
            type="button"
            className={cx(
              SIDEBAR_RECENTS_ACTION_BTN,
              "text-puntt-neutral-gray-11 hover:bg-puntt-red-9 hover:text-base-white",
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRequestDelete(chat._id);
            }}
            aria-label={t("nav.deleteChat")}
            data-testid="sidebar-ask-puntt-delete-recent-chat"
          >
            <Trash size={10} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function AskV2SidebarRecents() {
  const { t } = useTranslation("common");
  const { open } = useSidebar();
  const pollInterval = open ? ASK_PUNTT_UNREAD_LIST_POLL_MS : false;
  const { data: projectsData } = useAskPunttProjects({
    scope: "all",
    includeReviewState: true,
    refetchInterval: pollInterval,
  });
  // why: default scope is owned-only, so omitting it hides chats shared with this user
  const { data: chatsData } = useAskPunttChats({
    scope: "all",
    refetchInterval: pollInterval,
  });
  const { matches } = useRouterState();
  const navigate = useNavigate();
  const deleteProjectMutation = useDeleteAskPunttProject();
  const deleteChatMutation = useDeleteAskPunttChat();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const [projectIdToDelete, setProjectIdToDelete] = useState<string | null>(
    null,
  );
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);

  if (!open) return null;

  const projects = projectsData?.projects ?? [];
  const chats = (chatsData?.chats ?? []).filter(
    (c) => c.workflowType !== "project",
  );

  const recents = [
    ...projects.map((project) => ({
      kind: "project" as const,
      id: project._id,
      time: toRecentTime(project.updatedAt),
      project,
    })),
    ...chats.map((chat) => ({
      kind: "chat" as const,
      id: chat._id,
      time: toRecentTime(chat.updatedAt),
      chat,
    })),
  ]
    .sort((a, b) => b.time - a.time)
    .slice(0, SIDEBAR_RECENTS_LIMIT);

  if (recents.length === 0) return null;

  const canDeleteProject = (project: {
    role?: "owner" | "member";
    ownerId?: string;
  }) =>
    project.role === "owner" ||
    (currentUserId != null &&
      project.ownerId != null &&
      project.ownerId === currentUserId);

  function handleDeleteProject(projectId: string) {
    const wasViewing = isAskV2ProjectNavActive(projectId, matches);
    deleteProjectMutation.mutate(projectId, {
      onSuccess: () => {
        setProjectIdToDelete(null);
        if (wasViewing) {
          void navigate({ to: ASK_V2_ASK_HOME_PATH });
        }
      },
    });
  }

  function handleDeleteChat(chatId: string) {
    const chat = chats.find((c) => c._id === chatId);
    const isViewing =
      chat != null ? isAskV2ChatNavActive(chat, matches) : false;
    deleteChatMutation.mutate(chatId, {
      onSuccess: () => {
        setChatIdToDelete(null);
        if (isViewing) {
          if (chat && isProjectPersonalChat(chat) && chat.projectId) {
            void navigate({
              to: ASK_V2_PROJECT_PATH,
              params: { projectId: chat.projectId },
            });
          } else {
            void navigate({ to: ASK_V2_ASK_HOME_PATH });
          }
        }
      },
    });
  }

  const projectToDelete = projects.find((p) => p._id === projectIdToDelete);
  const chatToDelete = chats.find((c) => c._id === chatIdToDelete);

  return (
    <>
      <nav
        aria-label={t("nav.recents")}
        data-testid="sidebar-ask-puntt-recents"
        className="flex min-h-0 flex-1 flex-col overflow-hidden p-2"
      >
        <div
          className={`shrink-0 ${SIDEBAR_SECTION_HEADER_INDENT} py-1.5 pr-2`}
          data-testid="sidebar-ask-puntt-recents-header"
        >
          <Text size="2" weight="bold" className="truncate px-0">
            {t("nav.recents")}
          </Text>
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          data-testid="sidebar-ask-puntt-recents-list"
        >
          {recents.map((item) => {
            if (item.kind === "project") {
              const project = item.project;
              return (
                <RecentProjectRow
                  key={`project-${project._id}`}
                  project={project}
                  active={isAskV2ProjectNavActive(project._id, matches)}
                  canModify={canDeleteProject(project)}
                  onRequestDelete={setProjectIdToDelete}
                />
              );
            }

            const chat = item.chat;
            // Ownership gates rename/delete and hides the owner's name. When the
            // current user id is unknown we must NOT assume ownership — that
            // fallback exposed controls on other users' shared chats.
            const isOwnChat =
              currentUserId != null && chat.userId === currentUserId;
            return (
              <RecentChatRow
                key={`chat-${chat._id}`}
                chat={chat}
                active={isAskV2ChatNavActive(chat, matches)}
                isOwnChat={isOwnChat}
                onRequestDelete={setChatIdToDelete}
              />
            );
          })}
        </div>
      </nav>

      <Dialog.Root
        open={projectIdToDelete != null}
        onOpenChange={(openDialog) => {
          if (!openDialog) setProjectIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>{t("nav.deleteProject")}?</Dialog.Title>
          <Dialog.Description>
            {projectToDelete
              ? t("nav.areYouSureDeleteProject", { name: projectToDelete.name })
              : t("nav.areYouSureDeleteProjectFallback")}
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setProjectIdToDelete(null)}
              data-testid="sidebar-delete-project-cancel"
            >
              {t("nav.cancel")}
            </Button>
            <Button
              color="red"
              onClick={() =>
                projectIdToDelete && handleDeleteProject(projectIdToDelete)
              }
              disabled={deleteProjectMutation.isPending}
              loading={deleteProjectMutation.isPending}
              data-testid="sidebar-delete-project-confirm"
            >
              <Trash />
              {t("nav.delete")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root
        open={chatIdToDelete != null}
        onOpenChange={(openDialog) => {
          if (!openDialog) setChatIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>{t("nav.deleteChat")}?</Dialog.Title>
          <Dialog.Description>
            {chatToDelete
              ? t("nav.areYouSureDeleteChat", { title: chatToDelete.title })
              : t("nav.areYouSureDeleteChatFallback")}
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setChatIdToDelete(null)}
              data-testid="sidebar-delete-chat-cancel"
            >
              {t("nav.cancel")}
            </Button>
            <Button
              color="red"
              onClick={() => chatIdToDelete && handleDeleteChat(chatIdToDelete)}
              disabled={deleteChatMutation.isPending}
              loading={deleteChatMutation.isPending}
              data-testid="sidebar-delete-chat-confirm"
            >
              <Trash />
              {t("nav.delete")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
